import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Trans } from 'react-i18next';
import { SellListing } from './useTipBuyer';
import ListingCard from '../../ListingCard';
import { ListingCardListing } from '../../ListingCard/types';

// The Tip-Buyer panel lists the user's own sell listings on the shared compact
// card. SellListing carries the full /api/user/sell-listings payload (address
// object, property_*, images) — map it onto the card's ListingCardListing shape.
function sellListingToCard(l: SellListing): ListingCardListing {
  const a = l.address;
  const rooms = l.property_rooms ? Number(l.property_rooms) : undefined;
  return {
    _id: l._id,
    building_id: l.building_id,
    isUserOwnEvent: true,
    could_sell: true,
    property_description: l.property_description,
    property_size: l.property_size,
    property_rooms: rooms !== undefined && !Number.isNaN(rooms) ? rooms : undefined,
    property_floor: l.property_floor,
    property_has_elevator: l.property_has_elevator,
    property_has_balcony: l.property_has_balcony,
    property_renovation_status: l.property_renovation_status,
    property_asking_price: l.property_asking_price,
    address: {
      street: a?.street ?? l.address_string ?? '',
      houseNumber: a?.houseNumber ?? '',
      city: a?.city ?? '',
      country: a?.country,
      postcode: a?.postcode,
    },
    images: l.images,
    updated_at: l.updated_at ?? '',
    verified: l.verified,
  };
}

// House icon SVG component
const HouseIcon: React.FC<{ size?: string; className?: string }> = ({ size = '16px', className }) => (
  <svg
    height={size}
    viewBox="0 0 512 512"
    width={size}
    xmlns="http://www.w3.org/2000/svg"
    fill="currentColor"
    className={className}
  >
    <path d="m498.195312 222.695312c-.011718-.011718-.023437-.023437-.035156-.035156l-208.855468-208.847656c-8.902344-8.90625-20.738282-13.8125-33.328126-13.8125-12.589843 0-24.425781 4.902344-33.332031 13.808594l-208.746093 208.742187c-.070313.070313-.140626.144531-.210938.214844-18.28125 18.386719-18.25 48.21875.089844 66.558594 8.378906 8.382812 19.445312 13.238281 31.277344 13.746093.480468.046876.964843.070313 1.453124.070313h8.324219v153.699219c0 30.414062 24.746094 55.160156 55.167969 55.160156h81.710938c8.28125 0 15-6.714844 15-15v-120.5c0-13.878906 11.289062-25.167969 25.167968-25.167969h48.195313c13.878906 0 25.167969 11.289063 25.167969 25.167969v120.5c0 8.285156 6.714843 15 15 15h81.710937c30.421875 0 55.167969-24.746094 55.167969-55.160156v-153.699219h7.71875c12.585937 0 24.421875-4.902344 33.332031-13.808594 18.359375-18.371093 18.367187-48.253906.023437-66.636719zm0 0" />
  </svg>
);

// Info/Warning icon SVG component
const InfoIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12 16H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

export interface TipBuyerMenuProps {
  isOpen: boolean;
  menuRef: React.RefObject<HTMLDivElement>;
  buttonRef: React.RefObject<HTMLButtonElement>;
  isLoggedIn?: boolean;
  isLoadingListings: boolean;
  dailyLimitReached: boolean;
  userSellListings: SellListing[];
  tippedListings: Set<string>;
  onSelectListing: (listing: SellListing) => void;
  onLoginClick: () => void;
  className?: string;
}

export const TipBuyerMenu: React.FC<TipBuyerMenuProps> = ({
  isOpen,
  menuRef,
  buttonRef,
  isLoggedIn,
  isLoadingListings,
  dailyLimitReached,
  userSellListings,
  tippedListings,
  onSelectListing,
  onLoginClick,
  className = '',
}) => {
  const [position, setPosition] = useState<{ top: number; left: number; opensDownward: boolean } | null>(null);
  const [isPositioned, setIsPositioned] = useState(false);

  // Calculate position and direction based on button location
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      // Reset positioned state when opening
      setIsPositioned(false);
      
      // Use double requestAnimationFrame to ensure layout is complete
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!buttonRef.current) return;
          
          const buttonRect = buttonRef.current.getBoundingClientRect();
          const menuWidth = 300; // Menu width from CSS
          const menuHeight = 353; // Approximate max menu height
          
          // Position menu to the right edge of the button
          let left = buttonRect.right - menuWidth;
          
          // Ensure menu doesn't go off-screen to the left
          if (left < 10) {
            left = 10;
          }
          
          // Ensure menu doesn't go off-screen to the right
          if (left + menuWidth > window.innerWidth - 10) {
            left = window.innerWidth - menuWidth - 10;
          }

          // Determine if menu should open downward
          const spaceAbove = buttonRect.top;
          const opensDownward = spaceAbove < menuHeight + 20;

          if (opensDownward) {
            // Position below the button
            setPosition({
              top: buttonRect.bottom + 6,
              left,
              opensDownward: true,
            });
          } else {
            // Position above the button - we'll adjust after render when we know menu height
            setPosition({
              top: buttonRect.top - 6, // Will be adjusted by CSS transform
              left,
              opensDownward: false,
            });
          }
          
          // Mark as positioned after setting position
          setIsPositioned(true);
        });
      });
    } else if (!isOpen) {
      // Reset when menu closes
      setPosition(null);
      setIsPositioned(false);
    }
  }, [isOpen, buttonRef]);

  if (!isOpen) return null;
  
  const menuOpensDownward = position?.opensDownward ?? false;

  const menuContent = (
    <div
      className={`tip-buyer-menu-wrapper tip-buyer-menu-wrapper--portal ${menuOpensDownward ? 'tip-buyer-menu-wrapper--downward' : ''} ${isPositioned ? 'tip-buyer-menu-wrapper--ready' : ''} ${className}`.trim()}
      ref={menuRef}
      style={{
        position: 'fixed',
        top: position?.top ?? 0,
        left: position?.left ?? 0,
        transform: menuOpensDownward ? 'none' : 'translateY(-100%)',
      }}
    >
      <div className="tip-buyer-menu" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="tip-buyer-menu__header">
          <HouseIcon size="16px" />
          <span><Trans i18nKey="tipBuyer.menuTitle">Suggest your home to the buyer</Trans></span>
        </div>

        {/* Daily limit reached banner */}
        {dailyLimitReached && !isLoadingListings && isLoggedIn && (
          <div className="tip-buyer-menu__limit-banner">
            <InfoIcon />
            <span><Trans i18nKey="tipBuyer.dailyLimitReached">You've used your daily tip. Try again tomorrow!</Trans></span>
          </div>
        )}

        {/* Login prompt for non-logged-in users */}
        {!isLoggedIn ? (
          <div className="tip-buyer-menu__login-prompt">
            <div className="tip-buyer-menu__login-prompt__icon">
              <HouseIcon size="24px" />
            </div>
            <p className="tip-buyer-menu__login-prompt__text">
              <Trans i18nKey="tipBuyer.loginPrompt">Login to suggest your properties to interested buyers</Trans>
            </p>
            <button
              className="tip-buyer-menu__login-prompt__button"
              onClick={onLoginClick}
            >
              <Trans i18nKey="signupLoginModal.loginOrSignup">Login or sign up</Trans>
            </button>
          </div>
        ) : isLoadingListings ? (
          <div className="tip-buyer-menu__loading">
            {/* Skeleton loading cards */}
            {[1].map((i) => (
              <div key={i} className="tip-buyer-menu__skeleton">
                <div className="tip-buyer-menu__skeleton__image" />
                <div className="tip-buyer-menu__skeleton__content">
                  <div className="tip-buyer-menu__skeleton__title" />
                  <div className="tip-buyer-menu__skeleton__subtitle" />
                </div>
              </div>
            ))}
          </div>
        ) : userSellListings.length === 0 ? (
          <div className="tip-buyer-menu__empty">
            <div className="tip-buyer-menu__empty__icon">
              <HouseIcon size="16px" />
            </div>
            <p className="tip-buyer-menu__empty__text">
              <Trans i18nKey="tipBuyer.emptyState">You don't have any properties for sale yet</Trans>
            </p>
          </div>
        ) : (
          <div className="tip-buyer-menu__listings">
            {userSellListings.map((listing, index) => {
              const isTipped = tippedListings.has(listing._id);
              const isDisabled = isTipped || dailyLimitReached;
              
              return (
                <button
                  key={listing._id}
                  className={`tip-buyer-menu__listing ${isTipped ? 'tip-buyer-menu__listing--tipped' : ''} ${dailyLimitReached ? 'tip-buyer-menu__listing--disabled' : ''}`}
                  onClick={() => !isDisabled && onSelectListing(listing)}
                  disabled={isDisabled}
                >
                  <ListingCard
                    size="small"
                    type="seller"
                    listing={sellListingToCard(listing)}
                  />
                  {isTipped && (
                    <div className="tip-buyer-menu__listing__tipped-badge">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <Trans i18nKey="tipBuyer.sent">Sent</Trans>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(menuContent, document.body);
};
